<template>
  <m-card
    :class="{ 'cursor-pointer': readOnly }"
    sr-label="View Medication Prescribed Elsewhere"
    @click="medicationStatementOverview()"
  >
    <div
      :class="[
        {
          clickable: !readOnly,
          'marked-incorrect': entry.data.isMarkedIncorrect,
        },
      ]"
      gap="1"
    >
      <m-layout-stack horizontal>
        <div class="flex-auto">
          <span><b>Medication prescribed elsewhere:</b> {{ getMainLine }}</span>
          <span
            v-if="entry.data.startDateIsNotEncounterDate || entry.data.startDateIsNotDocumentDate"
          >
            • Started {{ entry.data.startDate }}</span
          >
          <span v-if="entry.data.isEnded"> • Ended {{ entry.data.endDate ?? "" }}</span>
        </div>
        <i v-if="entry.data.isRetrospectivelyAmended" style="white-space: nowrap">{{
          formatRetrospectivelyAmendedLabel
        }}</i>
      </m-layout-stack>
      <div
        v-if="
          entry.data.hiddenFromPatientFacingServices ||
          entry.data.confidentialFromThirdParties ||
          entry.data.linkedProblems.length
        "
      >
        <span class="secondary-text">
          {{ getConfidentialityTextAndLinkedProblems() }}
        </span>
      </div>
      <m-separator v-if="entry.data.additionalInformation" class="q-my-xs" />
      <span v-if="entry.data.additionalInformation">
        {{ entry.data.additionalInformation }}
      </span>
    </div>
  </m-card>
</template>

<script setup lang="ts">
import { openSlideover } from "../../../../composables/dialog/drawer";
import { PatientJournalRecord } from "../types";
import { computed } from "vue";
import MSeparator from "../../MSeparator";

const props = defineProps<{
  entry: PatientJournalRecord;
  readOnly: boolean;
  clickable?: boolean;
}>();

function medicationStatementOverview() {
  return props.readOnly
    ? openSlideover(
        `/clinical/medication-statement/overview-${props.entry.data.entryType}/${props.entry.id}`,
        { close: true },
      )
    : undefined;
}

const formatRetrospectivelyAmendedLabel = computed(() => {
  let label = [];
  if (props.entry.data.isRetrospectivelyAmended) {
    label.push("Edited");
  }

  return label.join(" • ");
});

const getMainLine = computed(() => {
  const textAsArray = [
    props.entry.data.productName,
    props.entry.data.dosageInstruction,
    props.entry.data.prescriptionType,
    props.entry.data.prescribedByOrganisation,
  ];
  const text = textAsArray.filter((x) => x).join(" • ");
  return text === "" ? false : text;
});

function getConfidentialityTextAndLinkedProblems() {
  const textArray = [];

  if (props.entry.data.hiddenFromPatientFacingServices) {
    textArray.push("Redacted from Patient Online Access");
  }
  if (props.entry.data.confidentialFromThirdParties) {
    textArray.push("Confidential from Third Parties");
  }

  const text = textArray.join(" • ");

  const problemsArray = props.entry.data.linkedProblems || [];
  const problemDescriptions = problemsArray
    .filter((x) => x.problemCodeDescription)
    .map((x) => x.problemCodeDescription);

  const problemText = problemDescriptions.length
    ? `Linked Problems: ${problemDescriptions.join(", ")}`
    : "";

  const finalTextArray = [text, problemText].filter(Boolean);

  return finalTextArray.length ? finalTextArray.join(" • ") : false;
}
</script>
